import {announce} from '@github-ui/aria-live'
import {isLoggedIn} from '@github-ui/client-env'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useAlive} from '@github-ui/use-alive'
import {BellIcon, BellSlashIcon} from '@primer/octicons-react'
import {ActionList, IconButton} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useCallback, useEffect, useRef} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {ALIVE_REASONS} from '../../../constants/alive'
import {LABELS} from '../../../constants/labels'
import {commitUpdateIssueSubscriptionMutation} from '../../../mutations/update-issue-subscription'
import type {IssueSecondaryNode} from '../../issue-viewer-secondary-query-types'
import {fetchSubscriptionStatus, type SubscriptionStatus, subscriptionStatusQueryKey} from './fetch-subscription-status'

// Plain fields formerly read via the leaf `StickySubscribeButtonFragment` (`id`, `threadSubscriptionChannel`)
// and the `StickySubscribeButtonRefetchableFragment` (`viewerThreadSubscriptionFormAction`, used to seed the
// TanStack query's initial subscription state). The parent (`StickyHeader`) still reads this via Relay, so
// widen with `RelayCompat` to accept Relay data.
// RELAY-CLEANUP(StickyHeader): remove the `RelayCompat` widening once the parent migrates.
export type StickySubscribeButtonFragment = RelayCompat<
  Pick<IssueSecondaryNode, 'id' | 'threadSubscriptionChannel' | 'viewerThreadSubscriptionFormAction'>
>

export type StickySubscribeButtonProps = {
  subscriptionData: StickySubscribeButtonFragment
  owner: string
  repo: string
  number: number
  /**
   * Render variant. Defaults to `'bell'` (icon button used in the sticky header).
   * Use `'menu-item'` to render the action as an ActionList.Item inside an overflow menu.
   */
  variant?: 'bell' | 'menu-item'
}

export function StickySubscribeButton({
  subscriptionData,
  owner,
  repo,
  number,
  variant = 'bell',
}: StickySubscribeButtonProps) {
  const {threadSubscriptionChannel, id, viewerThreadSubscriptionFormAction} = subscriptionData

  if (!isLoggedIn() || !threadSubscriptionChannel) return null

  return (
    <StickySubscribeButtonInternal
      id={id}
      owner={owner}
      repo={repo}
      number={number}
      threadSubscriptionChannel={threadSubscriptionChannel}
      initialFormAction={viewerThreadSubscriptionFormAction ?? null}
      variant={variant}
    />
  )
}

function StickySubscribeButtonInternal({
  id,
  owner,
  repo,
  number,
  threadSubscriptionChannel,
  initialFormAction,
  variant,
}: {
  id: string
  owner: string
  repo: string
  number: number
  threadSubscriptionChannel: string
  initialFormAction: SubscriptionStatus['viewerThreadSubscriptionFormAction']
  variant: 'bell' | 'menu-item'
}) {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const shouldUpdateAnnouncement = useRef(false)

  // we seed this from the parent's plain props (`initialData`), so there is no fetch on mount.
  // `staleTime: Infinity` keeps the seed authoritative until we explicitly refetch via
  // `invalidateQueries` after a subscribe/unsubscribe or an Alive channel event.
  const {data} = useQuery({
    queryKey: subscriptionStatusQueryKey(owner, repo, number),
    queryFn: ({signal}) => fetchSubscriptionStatus(owner, repo, number, signal),
    initialData: {
      id,
      viewerThreadSubscriptionFormAction: initialFormAction,
      viewerCustomSubscriptionEvents: null,
    },
    staleTime: Infinity,
  })
  const subscribed = data.viewerThreadSubscriptionFormAction === 'UNSUBSCRIBE'

  const announceSubscriptionUpdates = useCallback((newSubscribed: boolean, success = true) => {
    if (success) {
      announce(
        newSubscribed ? LABELS.notifications.subscribedAnnouncement : LABELS.notifications.unsubscribedAnnouncement,
      )
    } else {
      announce(LABELS.somethingWentWrong)
    }
  }, [])

  useEffect(() => {
    if (shouldUpdateAnnouncement.current) {
      announceSubscriptionUpdates(subscribed)
      shouldUpdateAnnouncement.current = false
    }
  }, [subscribed, announceSubscriptionUpdates])

  const refreshSubscription = useCallback(() => {
    shouldUpdateAnnouncement.current = true
    void queryClient.invalidateQueries({queryKey: subscriptionStatusQueryKey(owner, repo, number)})
  }, [queryClient, owner, repo, number])

  useAlive(threadSubscriptionChannel, (event: {reason: string}) => {
    if (!event?.reason) return
    if (event.reason === ALIVE_REASONS.SUBSCRIBED && !subscribed) {
      refreshSubscription()
    } else if (event.reason === ALIVE_REASONS.UNSUBSCRIBED && subscribed) {
      refreshSubscription()
    }
  })

  const toggleSubscription = useCallback(() => {
    const newSubscribed = !subscribed
    const newFormAction: SubscriptionStatus['viewerThreadSubscriptionFormAction'] = newSubscribed
      ? 'UNSUBSCRIBE'
      : 'SUBSCRIBE'

    // as this button no longer reads from the relay store, mirror the optimistic update into the cache.
    // a plain subscribe/unsubscribe clears custom events server-side, so reset viewerCustomSubscriptionEvents too.
    // the query key is shared with SubscriptionSection, which reads custom events to preselect the "Custom" dialog option.
    const queryKey = subscriptionStatusQueryKey(owner, repo, number)
    const previousData = queryClient.getQueryData<SubscriptionStatus>(queryKey)
    queryClient.setQueryData<SubscriptionStatus>(queryKey, prev =>
      prev ? {...prev, viewerThreadSubscriptionFormAction: newFormAction, viewerCustomSubscriptionEvents: null} : prev,
    )

    commitUpdateIssueSubscriptionMutation({
      environment,
      input: {
        subscribableId: id,
        state: newSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
      },
      onCompleted: () => announceSubscriptionUpdates(newSubscribed),
      onError: () => {
        queryClient.setQueryData(queryKey, previousData)
        announceSubscriptionUpdates(subscribed, false)
      },
    })
  }, [environment, id, owner, repo, number, subscribed, queryClient, announceSubscriptionUpdates])

  const menuItemLabel = subscribed ? LABELS.notifications.subscribedButton : LABELS.notifications.unsubscribedButton
  const tooltipText = subscribed
    ? `${LABELS.notifications.subscribedDescription} Click to unsubscribe`
    : `${LABELS.notifications.unsubscribedDescription} Click to subscribe`

  if (variant === 'menu-item') {
    return (
      <ActionList.Item onSelect={toggleSubscription}>
        <ActionList.LeadingVisual>{subscribed ? <BellIcon /> : <BellSlashIcon />}</ActionList.LeadingVisual>
        {menuItemLabel}
      </ActionList.Item>
    )
  }

  // Use a static aria-label with aria-pressed so screen readers announce the
  // toggle state once (e.g. "Notifications, toggle button, pressed") rather
  // than the confusing "Unsubscribe, toggle button, pressed" combination that
  // results from changing the label and the pressed state together.
  // See WAI-ARIA APG toggle button guidance.
  return (
    <Tooltip type="description" text={tooltipText} direction="s">
      <IconButton
        icon={subscribed ? BellIcon : BellSlashIcon}
        aria-label={LABELS.notifications.notificationsTitle}
        aria-pressed={subscribed}
        variant="invisible"
        onClick={toggleSubscription}
      />
    </Tooltip>
  )
}
